import { SORTING_ORDERS, TABLE_PER_PAGES_OPTIONS } from "@/constants";

/**
 * Pagination and Sort mixin
 * @description Will be used for server side pagination and sorting
 * @author {Jatin Kamboj}
 */
export default {
  name: "PaginationSortMixin",
  /**
   * ---------------- Custom events ------------------
   */
  emits: ["get-records"],
  /**
   * -------------- Data Properties -------------
   */
  data() {
    return {
      // Default table footer settings
      footerProps: {
        itemsPerPageOptions: [...TABLE_PER_PAGES_OPTIONS],
        itemsPerPage: TABLE_PER_PAGES_OPTIONS[3],
      },
      // Pagination property responsible for table pagination
      pagination: { sortDesc: [], sortBy: [] },
      disablePaginationWatcher: false,
    };
  },
  /**
   * -------------- Watch Properties -------------
   */
  watch: {
    /**
     * Watches pagination property defined in the parent component for a change
     */
    pagination() {
      // Stops watcher to fetch updated user records
      if (this.disablePaginationWatcher) return;
      // Load records when records are not loading
      !this.isLoading && this.getRecords();
    },
  },
  /**
   * -------------- Methods -------------
   */
  methods: {
    /**
     * Emits custom event in parent component to fetch the latest records
     * @param {Object} props Params to be used in api call
     * @emits get-records Emits this event in the parent component
     */
    getRecords(props = {}) {
      this.$emit("get-records", { ...this.updateTableSettings(), ...props });
    },
    /**
     * Update user table sorting on the basis of table columns selected by the user
     */
    updateTableSettings() {
      let [sort_by] = this.pagination.sortBy;
      let [sort_order] = this.pagination.sortDesc;

      sort_order = sort_order ? SORTING_ORDERS.desc : SORTING_ORDERS.asc;
      return {
        page: this.pagination?.page ?? 1,
        per_page: this.pagination.itemsPerPage || 10,
        "q[s]": `${sort_by ?? (this.defaultSortOrder || "")} ${sort_order}`,
      };
    },
  },
  /**
   * ---------------- Computed properties ------------------
   */
  computed: {
    /**
     * Are new records present for the table
     * @type {Boolean}
     */
    hasRecords() {
      if (!this.data) return false;
      return this.data?.length >= this.pagination.itemsPerPage;
    },
    /**
     * Server side table pagination config
     * @description {{data}} property (Table component prop) contains an array of records to be shown in the table
     * @type {Object}
     */
    paginationMeta() {
      return {
        itemsPerPage: this.pagination.itemsPerPage || 10,
        serverItemsLength: this.serverItemsLength,
      };
    },
    /**
     * Computes server side remaining items
     * @returns {Number} No of records present
     */
    serverItemsLength() {
      return this.hasRecords
        ? this.pagination.page * this.data?.length +
            this.pagination.itemsPerPage
        : this.data?.length;
    },
  },

  /**
   * -------------- Mounted lifecycle hook -------------
   */
  mounted() {
    // Pushes default sort order of the table defined in the component
    this.pagination.sortBy.push(this.defaultSortOrder ?? "");
    this.pagination.sortDesc.push(this.sortDesc ?? false);
  },
};
